/* eslint-disable max-lines */
/* eslint no-nested-ternary: 1 */
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Link from '@material-ui/core/Link';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import FlagIcon from '@material-ui/icons/Flag';
import VideocamIcon from '@material-ui/icons/Videocam';

import AccessLevelService, {
  gold,
  guest,
  verifiedRoomModerator,
} from 'services/AccessLevelService';
import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import NotificationService from 'services/NotificationService';
import ProfileService from 'services/ProfileService';
import StreamingService from 'services/StreamingService';
import BroadcasterNotesStore from 'stores/BroadcasterNotesStore';
import BroadcasterStore from 'stores/BroadcasterStore';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import GroupShowStore from 'stores/GroupShowStore';
import ModerationStore from 'stores/ModerationStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import { ACCESS_LEVEL_STR } from 'pages/User/AccessLevel';
import {
  ENUM_FAVOURITE_STATUS,
  ENUM_FRIEND_STATUS,
} from 'enum/profile';
import routeDefinitions from 'enum/routeDefinitions';
import allUserGroups from 'enum/userGroups';
import {
  BROADCAST_STATUS,
  NEW_SHOWS_STATE,
  VIDEO_AND_CHAT_CLASS,
} from 'enum/videoPlayer';
import { isMyProfileHelperFunc } from 'utils/helpers';
import {
  CONTEXTUAL_GOLD_MODAL_AREAS,
  MODERATION_OPTIONS,
  TabPanelType,
} from 'utils/consts';

import styles from './ContextualMenu.css';


// eslint-disable-next-line complexity
const ContextualMenu = observer((props) => {
  const {
    anchorEl,
    originalUsername,
    showContextualMenu,
    close,
    canDeleteConversation,
    deleteConversation,
    listItemUserAccessLevel,
    ableToPeek,
    handlePeek,
    listedUserAccessLevel,
    isBroadcasterChat,
    isFriendsTab,
    isFavouritesTab,
    message,
    profileImage,
    isRoomModerator,
    isSilenced,
    isUserList,
    isConversation,
    handleDrawerClose,
    confirmConversationDelete,
  } = props;
  const {
    profileName: performerName,
    performerDetails,
    isUserSilenced,
    silenceOrBanOrKickUser,
    getFeaturedCamStats,
    broadcastStatus,
  } = ProfileStore;
  const {
    togglePrivateMessages,
    setProfileChatTabValue,
    setActiveConversation,
    activeConversationNames,
    profileChatTabValue,
    chatSettings: { allowPrivateMessagesFromOtherViewers },
  } = ChatStore;
  const { allNotes } = BroadcasterNotesStore;
  const { user, hasAdminPrivileges } = UserStore;
  const {
    isLoggedInUserRoomModerator,
    authData: { username },
    isFanClubSubscribed,
    isUserSilencedByRoomModerator,
    newShowsState,
    isUserInRoster,
  } = FirebaseStore;

  const [spamMarkActive, setSpamMarkActive] = useState(false);
  const [spamMarkDisabled, setSpamMarkDisabled] = useState(false);

  const { accessHash, accessLevel, username: loggedInUserUsername } = user;
  const profileName = isBroadcasterChat ? username : performerName;
  const { friendStatus, favoriteStatus } = BroadcasterStore;
  const isMinVerifiedRoomModerator = AccessLevelService.isMin(verifiedRoomModerator, accessLevel);
  const isGuestUser = listedUserAccessLevel === guest;
  const accessLevels46orAbove = [
    ACCESS_LEVEL_STR.VERIFIED_ROOM_MODERATOR,
    ACCESS_LEVEL_STR.COACH,
    ACCESS_LEVEL_STR.USER_MODERATOR,
    ACCESS_LEVEL_STR.MODERATOR,
    ACCESS_LEVEL_STR.ADMIN,
  ];
  const hasAccessLevel46orAbove =
    listItemUserAccessLevel && accessLevels46orAbove.find((al) => al === listItemUserAccessLevel);
  const broadcasterAsViewer =
    UserStore.hasCF(allUserGroups.BROADCASTER_AS_VIEWER) &&
    isMyProfileHelperFunc(user, profileName);
  const amIRoomModerator = broadcasterAsViewer || isLoggedInUserRoomModerator(loggedInUserUsername);
  const isTargetedUserRoomMod = isLoggedInUserRoomModerator(originalUsername);
  const areBothRoomMods = amIRoomModerator && isTargetedUserRoomMod;
  const showPublicChatCF = UserStore.hasCF(allUserGroups.PROFILES_V2_SPECIAL_SHOW_PUBLIC_CHAT);
  const isGsAlternateChatOn =
    showPublicChatCF &&
    GroupShowStore.userHaveTicket(loggedInUserUsername) &&
    broadcastStatus === BROADCAST_STATUS.INSIDE_GS &&
    newShowsState === NEW_SHOWS_STATE.INSIDE_GS;
  const isFriend = friendStatus === ENUM_FRIEND_STATUS.ACCEPTED;
  const friendRequestSent = friendStatus === ENUM_FRIEND_STATUS.PENDING;
  const isFavorite = favoriteStatus === ENUM_FAVOURITE_STATUS.FAVORITE;
  const isPerformer =
    performerDetails.username && originalUsername.toLowerCase() === performerDetails.username.toLowerCase();
  const isRoomModeratorClickedOnPerf = hasAdminPrivileges && isPerformer;
  const ableToKickAndBan =
    ((hasAdminPrivileges && !isRoomModeratorClickedOnPerf) ||
      (amIRoomModerator && !isPerformer && !hasAccessLevel46orAbove)) &&
    !areBothRoomMods;

  useEffect(() => {
    if (isMinVerifiedRoomModerator && isPerformer && showContextualMenu) {
      getFeaturedCamStats(profileName);
    }
  }, [getFeaturedCamStats, profileName, isMinVerifiedRoomModerator, isPerformer, showContextualMenu]);

  useEffect(() => {
    if (isBroadcasterChat && showContextualMenu) {
      BroadcasterStore.getPerformerFriendsStatus(username, originalUsername);
      BroadcasterStore.getPerformerFavoriteStatus(username, originalUsername);
    }
  }, [isBroadcasterChat, username, originalUsername, showContextualMenu]);

  useEffect(() => {
    window.addEventListener('scroll', close, { passive: true });

    return () => window.removeEventListener('scroll', close);
  }, [close]);

  const {
    CHAT_CONTEXTUAL_MENU_CONFIRM_OPTION,
    CHAT_CONTEXTUAL_MENU_KICK,
    CHAT_CONTEXTUAL_MENU_BAN,
    CHAT_CONTEXTUAL_MENU_PERMA_BAN,
    CHAT_CONTEXTUAL_MENU_FAN_BAN,
    CHAT_CONTEXTUAL_MENU_FAN_PERMA_BAN,
    CHAT_CONTEXTUAL_MENU_DELETE_KICK,
    CHAT_CONTEXTUAL_MENU_CLOSE_ROOM,
    CHAT_CONTEXTUAL_MENU_FEATURE_BROADCASTER,
    CHAT_CONTEXTUAL_MENU_REPORT,
  } = ModalService.MODAL_KEYS;

  const openConfirmModal = useCallback(() => {
    ModalService.showModal(CHAT_CONTEXTUAL_MENU_CONFIRM_OPTION, {
      show: true,
      originalUsername,
      isLoggedInUserRoomModerator: isMinVerifiedRoomModerator || amIRoomModerator,
      username,
      accessLevel,
      accessHash,
      profileName,
      isBroadcasterChat,
    });
  }, [
    CHAT_CONTEXTUAL_MENU_CONFIRM_OPTION,
    accessHash,
    accessLevel,
    amIRoomModerator,
    isBroadcasterChat,
    isMinVerifiedRoomModerator,
    originalUsername,
    profileName,
    username,
  ]);

  const openKickModal = useCallback(() => {
    ModalService.showModal(CHAT_CONTEXTUAL_MENU_KICK, {
      show: true,
      originalUsername,
      isLoggedInUserRoomModerator: hasAdminPrivileges || amIRoomModerator,
      username,
      accessLevel,
      accessHash,
      profileName,
      isBroadcasterChat,
    });
    close();
  }, [
    CHAT_CONTEXTUAL_MENU_KICK,
    accessHash,
    accessLevel,
    amIRoomModerator,
    close,
    hasAdminPrivileges,
    isBroadcasterChat,
    originalUsername,
    profileName,
    username,
  ]);

  const openBanModal = useCallback(() => {
    ModalService.showModal(CHAT_CONTEXTUAL_MENU_BAN, {
      show: true,
      originalUsername,
      isLoggedInUserRoomModerator: hasAdminPrivileges || amIRoomModerator,
      username,
      accessLevel,
      accessHash,
      profileName,
      isBroadcasterChat,
      confirmConversationDelete,
    });
    close();
  }, [
    CHAT_CONTEXTUAL_MENU_BAN,
    accessLevel,
    accessHash,
    amIRoomModerator,
    close,
    confirmConversationDelete,
    hasAdminPrivileges,
    isBroadcasterChat,
    originalUsername,
    profileName,
    username,
  ]);

  const openPermaBanModal = useCallback(() => {
    ModalService.showModal(CHAT_CONTEXTUAL_MENU_PERMA_BAN, {
      show: true,
      originalUsername,
      isLoggedInUserRoomModerator: hasAdminPrivileges || amIRoomModerator,
      username,
      accessLevel,
      accessHash,
      profileName,
      isDesktop: true,
      isBroadcasterChat,
    });
    close();
  }, [
    CHAT_CONTEXTUAL_MENU_PERMA_BAN,
    accessHash,
    accessLevel,
    amIRoomModerator,
    close,
    hasAdminPrivileges,
    isBroadcasterChat,
    originalUsername,
    profileName,
    username,
  ]);

  const openFanBanModal = useCallback(() => {
    ModalService.showModal(CHAT_CONTEXTUAL_MENU_FAN_BAN, {
      show: true,
      originalUsername,
      isLoggedInUserRoomModerator: hasAdminPrivileges || amIRoomModerator,
      username,
      accessLevel,
      accessHash,
      profileName,
      isBroadcasterChat,
      confirmConversationDelete,
    });
    close();
  }, [
    CHAT_CONTEXTUAL_MENU_FAN_BAN,
    accessHash,
    accessLevel,
    amIRoomModerator,
    close,
    confirmConversationDelete,
    hasAdminPrivileges,
    isBroadcasterChat,
    originalUsername,
    profileName,
    username,
  ]);

  const openFanPermaBanModal = useCallback(() => {
    ModalService.showModal(CHAT_CONTEXTUAL_MENU_FAN_PERMA_BAN, {
      show: true,
      originalUsername,
      isLoggedInUserRoomModerator: hasAdminPrivileges || amIRoomModerator,
      username,
      accessLevel,
      accessHash,
      profileName,
    });
    close();
  }, [
    CHAT_CONTEXTUAL_MENU_FAN_PERMA_BAN,
    accessHash,
    accessLevel,
    amIRoomModerator,
    close,
    hasAdminPrivileges,
    originalUsername,
    profileName,
    username,
  ]);

  const openDeleteKickModal = useCallback(() => {
    ModalService.showModal(CHAT_CONTEXTUAL_MENU_DELETE_KICK, {
      show: true,
      originalUsername,
      isLoggedInUserRoomModerator: hasAdminPrivileges || amIRoomModerator,
      username,
      accessLevel,
      accessHash,
      profileName,
      isDesktop: true,
    });
    close();
  }, [
    CHAT_CONTEXTUAL_MENU_DELETE_KICK,
    accessHash,
    accessLevel,
    amIRoomModerator,
    close,
    hasAdminPrivileges,
    originalUsername,
    profileName,
    username,
  ]);

  const openCloseRoomModal = useCallback(() => {
    ModalService.showModal(CHAT_CONTEXTUAL_MENU_CLOSE_ROOM, {
      show: true,
      originalUsername,
      isLoggedInUserRoomModerator: hasAdminPrivileges || amIRoomModerator,
      username,
      accessLevel,
      accessHash,
      profileName,
    });
    close();
  }, [
    CHAT_CONTEXTUAL_MENU_CLOSE_ROOM,
    accessHash,
    accessLevel,
    amIRoomModerator,
    close,
    hasAdminPrivileges,
    originalUsername,
    profileName,
    username,
  ]);

  const openFeatureBroadcasterModal = useCallback(() => {
    ModalService.showModal(CHAT_CONTEXTUAL_MENU_FEATURE_BROADCASTER, {
      show: true,
      originalUsername,
      isLoggedInUserRoomModerator: hasAdminPrivileges || amIRoomModerator,
      username,
      accessLevel,
      accessHash,
      profileName,
    });
    close();
  }, [
    CHAT_CONTEXTUAL_MENU_FEATURE_BROADCASTER,
    accessHash,
    accessLevel,
    amIRoomModerator,
    close,
    hasAdminPrivileges,
    originalUsername,
    profileName,
    username,
  ]);

  const openReportModal = useCallback(() => {
    handleDrawerClose?.();
    ModalService.showModal(CHAT_CONTEXTUAL_MENU_REPORT, {
      show: true,
      originalUsername,
      isBroadcasterChat,
    });
    close();
  }, [
    CHAT_CONTEXTUAL_MENU_REPORT,
    close,
    handleDrawerClose,
    isBroadcasterChat,
    originalUsername,
  ]);

  const handleSilenceUnsilence = useCallback(() => {
    let isUserRoomSilencedOrAccountSilenced = undefined;
    if (amIRoomModerator || isMinVerifiedRoomModerator) {
      isUserRoomSilencedOrAccountSilenced = isUserSilencedByRoomModerator(originalUsername);
    } else {
      isUserRoomSilencedOrAccountSilenced = isUserSilenced(originalUsername);
    }

    if (isUserRoomSilencedOrAccountSilenced) {
      silenceOrBanOrKickUser(
        isLoggedInUserRoomModerator,
        originalUsername,
        username,
        false,
        accessHash,
        accessLevel,
        'unsilence',
        profileName
      );
    } else {
      openConfirmModal();
    }

    close();
  }, [
    accessHash,
    accessLevel,
    amIRoomModerator,
    close,
    isLoggedInUserRoomModerator,
    isMinVerifiedRoomModerator,
    isUserSilenced,
    isUserSilencedByRoomModerator,
    openConfirmModal,
    originalUsername,
    profileName,
    silenceOrBanOrKickUser,
    username,
  ]);

  const handleBroadcasterPrivateMessage = useCallback(() => {
    ChatStore.setBroadcasterChatTab(TabPanelType.privateChat);
    setActiveConversation(originalUsername);
  }, [originalUsername, setActiveConversation]);

  const handlePerformerPrivateMsg = useCallback(() => {
    togglePrivateMessages(true);
    setProfileChatTabValue(isGsAlternateChatOn ? 2 : 1);
  }, [isGsAlternateChatOn, setProfileChatTabValue, togglePrivateMessages]);

  const handleNotPerformerPrivateMessage = useCallback(() => {
    if (AccessLevelService.isMin(gold, accessLevel) || activeConversationNames.includes(originalUsername)) {
      setProfileChatTabValue(isGsAlternateChatOn ? 3 : 2);
      if (allowPrivateMessagesFromOtherViewers) {
        setActiveConversation(originalUsername);
      }
    } else {
      ModalService.showModal(
        ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_MODAL,
        { area: CONTEXTUAL_GOLD_MODAL_AREAS.PRIVATE_MESSAGING }
      );
    }
  }, [
    accessLevel,
    activeConversationNames,
    isGsAlternateChatOn,
    originalUsername,
    allowPrivateMessagesFromOtherViewers,
    setActiveConversation,
    setProfileChatTabValue,
  ]);

  const handlePrivateMessage = useCallback(() => {
    close();
    if (isBroadcasterChat) {
      handleBroadcasterPrivateMessage();
    } else {
      // eslint-disable-next-line no-unused-expressions
      (isPerformer || broadcasterAsViewer) ? handlePerformerPrivateMsg() : handleNotPerformerPrivateMessage();
    }
  }, [
    broadcasterAsViewer,
    close,
    handleBroadcasterPrivateMessage,
    handleNotPerformerPrivateMessage,
    handlePerformerPrivateMsg,
    isBroadcasterChat,
    isPerformer,
  ]);

  const handleMarkAsSpam = useCallback(() => {
    close();
    if (!spamMarkActive) {
      setSpamMarkDisabled(true);
      ProfileService.markAsSpamMessage({
        recipient: message.senderOriginalUsername,
        type: 'markAsSpam',
        reason: message?.message,
        accessHash,
        username: loggedInUserUsername,
        roomname: loggedInUserUsername,
      })
        .then((response) => {
          if (response.data.status === 'ok') {
            NotificationService.create({
              type: NotificationService.type.TOAST,
              text: translate('v2broadcast.marked.as.spam'),
              subtype: NotificationService.subtype.SUCCESS,
            });
            setSpamMarkActive(true);
          } else {
            throw Error();
          }
        })
        .catch(() => {
          setSpamMarkDisabled(false);
          NotificationService.create({
            type: NotificationService.type.TOAST,
            text: translate('v2profile.notification.error.default'),
            subtype: NotificationService.subtype.ERROR,
          });
        });
    }
  }, [
    accessHash,
    close,
    loggedInUserUsername,
    message?.message,
    message?.senderOriginalUsername,
    spamMarkActive,
  ]);

  const { inviteToRoom } = StreamingService;

  const addFriend = useCallback(() => {
    let status = undefined;
    if (isFriend) {
      status = ENUM_FRIEND_STATUS.ACCEPTED;
    } else if (friendRequestSent) {
      status = ENUM_FRIEND_STATUS.PENDING;
    } else {
      status = ENUM_FRIEND_STATUS.NONE;
    }

    ProfileStore.handleAddFriend(loggedInUserUsername, originalUsername, status);
    close();
  }, [
    close,
    isFriend,
    loggedInUserUsername,
    originalUsername,
    friendRequestSent,
  ]);

  const addFavorite = useCallback(() => {
    const status = isFavorite ? ENUM_FAVOURITE_STATUS.FAVORITE : ENUM_FAVOURITE_STATUS.NOT_FAVORITE;
    ProfileStore.handleToggleFavorite(loggedInUserUsername, originalUsername, status);
    close();
  }, [
    close,
    isFavorite,
    loggedInUserUsername,
    originalUsername,
  ]);

  const handleModerator = useCallback(() => {
    close();
    const isContextualMenu = true;
    if (parseInt(isRoomModerator, 10) === 1) {
      ModerationStore.removeModerator(
        {
          recipient: originalUsername,
          roomname: loggedInUserUsername,
          username: loggedInUserUsername,
          accessHash,
          type: MODERATION_OPTIONS.REMOVE_MODERATOR,
        },
        isContextualMenu
      );
    } else {
      ModerationStore.setModerator(
        {
          recipient: originalUsername,
          roomname: loggedInUserUsername,
          username: loggedInUserUsername,
          accessHash,
          type: MODERATION_OPTIONS.MODERATOR,
        },
        isContextualMenu
      );
    }
  }, [
    accessHash,
    isRoomModerator,
    originalUsername,
    loggedInUserUsername,
    close,
  ]);

  const handleBroadcasterSilence = useCallback(() => {
    handleDrawerClose?.();
    close();
    if (isSilenced) {
      const isContextualMenu = true;
      ModerationStore.removeSilence(
        {
          recipient: originalUsername,
          roomname: loggedInUserUsername,
          username: loggedInUserUsername,
          accessHash,
          type: MODERATION_OPTIONS.UNSILENCE,
        },
        isContextualMenu
      );
    } else {
      openConfirmModal();
    }
  }, [
    accessHash,
    close,
    handleDrawerClose,
    isSilenced,
    loggedInUserUsername,
    openConfirmModal,
    originalUsername,
  ]);

  const handleBroadcasterKick = useCallback(() => {
    handleDrawerClose?.();
    close();
    openKickModal();
  }, [openKickModal, close, handleDrawerClose]);

  const openBroadcasterBanModal = useCallback(() => {
    handleDrawerClose?.();
    close();
    if (isFanClubSubscribed(originalUsername)) {
      openFanBanModal();
    } else {
      openBanModal();
    }
  }, [
    close,
    handleDrawerClose,
    isFanClubSubscribed,
    originalUsername,
    openBanModal,
    openFanBanModal,
  ]);

  const handleNewNoteModal = useCallback(() => {
    handleDrawerClose?.();
    close();
    ModalService.showModal(
      ModalService.MODAL_KEYS.NEW_NOTE_MODAL,
      { targetName: originalUsername }
    );
  }, [originalUsername, handleDrawerClose, close]);

  const peekClickHandle = useCallback(() => {
    close();
    handlePeek();
  }, [handlePeek, close]);

  const openInboxModal = useCallback(() => {
    close();
    ModalService.showModal(
      ModalService.MODAL_KEYS.PRIVATE_MESSAGE_MODAL,
      {
        profilePicture: profileImage,
        receiverUsername: originalUsername,
        user,
      }
    );
  }, [originalUsername, profileImage, user, close]);

  const isAssignedRoomModeratorTargetedAdminOrBroadcaster = Boolean(
    amIRoomModerator &&
    (isPerformer || hasAccessLevel46orAbove)
  );

  const isUserTargetingPerformerCoachOrRoomMod = Boolean(
    !hasAdminPrivileges &&
    (isPerformer || hasAccessLevel46orAbove || isLoggedInUserRoomModerator(originalUsername))
  );

  const onInviteRoom = useCallback(() => {
    inviteToRoom(username, accessHash, originalUsername);
  }, [username, accessHash, originalUsername, inviteToRoom]);

  const hasSilenceOption = Boolean(
    !isGuestUser &&
    !isAssignedRoomModeratorTargetedAdminOrBroadcaster &&
    !isUserTargetingPerformerCoachOrRoomMod
  );

  const videoAndChatWrap = document.querySelector(`.${VIDEO_AND_CHAT_CLASS}`);

  let isPrivateChatWithPerformerOpen = undefined;
  if (isGsAlternateChatOn) {
    isPrivateChatWithPerformerOpen = (profileChatTabValue === 2);
  } else {
    isPrivateChatWithPerformerOpen = (profileChatTabValue === 1);
  }

  const showSendInboxMessage = Boolean(
    !isFavouritesTab &&
    !isUserList
  );

  let friendTxt = undefined;
  if (isFriend) {
    friendTxt = translate('v2broadcast.remove.friend');
  } else if (friendRequestSent) {
    friendTxt = translate('v2broadcast.cancel.friend.request');
  } else {
    friendTxt = translate('v2broadcast.add.friend');
  }

  let silenceTxtMod = undefined;
  const isMod = parseInt(isRoomModerator, 10) === 1;
  if (isUserSilencedByRoomModerator(originalUsername)) {
    silenceTxtMod = translate('v2profile.v2.unsilence');
  } else {
    silenceTxtMod = translate('v2profile.silence.user');
  }

  let silenceTxt = undefined;
  if (isUserSilenced(originalUsername)) {
    silenceTxt = translate('v2profile.v2.unsilence');
  } else {
    silenceTxt = translate('v2profile.silence.user');
  }

  const notMe = FirebaseStore.authData.username !== originalUsername;

  return notMe ? (
    <Menu
      anchorEl={anchorEl}
      classes={{
        list: styles.ContextualMenu,
        paper: styles.ContextualMenuWrapper,
      }}
      container={videoAndChatWrap}
      elevation={0}
      open={showContextualMenu}
      onClose={close}
    >
      {!isGuestUser && (
        <div>
          <MenuItem classes={{ root: styles.selectedUsername }} disabled={true}>
            {originalUsername}
          </MenuItem>
          {!isPerformer && (
            <MenuItem>
              <Link
                href={routeDefinitions.get(routeDefinitions.profileV2.root, { user: originalUsername })}
                rel="noopener noreferrer"
                style={{ width: '100%' }}
                target="_blank"
              >
                {translate('v2profile.visit.profile')}
              </Link>
            </MenuItem>
          )}
          {isUserInRoster(originalUsername) && !isPrivateChatWithPerformerOpen && (
            <MenuItem onClick={handlePrivateMessage}>{translate('v2profile.v2profile.privateMessage.title')}</MenuItem>
          )}
        </div>
      )}

      {ableToPeek && !isGuestUser && !isFriendsTab && !isFavouritesTab && (
        <MenuItem classes={{ root: styles.peekRoot }} onClick={peekClickHandle}>
          <VideocamIcon />
          {translate('profile.v2.studio.user.peek')}!
        </MenuItem>
      )}

      {isBroadcasterChat && (
        <div>
          <MenuItem onClick={addFriend}>
            {friendTxt}
          </MenuItem>
          <MenuItem onClick={addFavorite}>
            {isFavorite ? translate('v2broadcast.remove.favorite') : translate('v2broadcast.add.favorite')}
          </MenuItem>
          {!allNotes[originalUsername] && (
            <MenuItem onClick={handleNewNoteModal}>{translate('v2broadcast.add.note')}</MenuItem>
          )}
        </div>
      )}

      {isBroadcasterChat && !isFriendsTab && !isFavouritesTab && (
        <div>
          <MenuItem onClick={handleModerator}>
            {isMod ? translate('v2broadcast.unassign.as.moderator') : translate('v2broadcast.assign.as.moderator')}
          </MenuItem>
          <MenuItem onClick={handleBroadcasterSilence}>
            {isSilenced ? translate('v2profile.v2.unsilence') : translate('v2profile.silence.user')}
          </MenuItem>
          <MenuItem onClick={handleBroadcasterKick}>{translate('v2profile.kick.user')}</MenuItem>
          <MenuItem onClick={openBroadcasterBanModal}>
            <div className={styles.option}>
              <span>{translate('v2profile.ban.user')}</span>
            </div>
          </MenuItem>
        </div>
      )}

      {hasSilenceOption && !isBroadcasterChat && (
        <MenuItem onClick={handleSilenceUnsilence}>
          {isMinVerifiedRoomModerator || amIRoomModerator ? (
            <span>
              {silenceTxtMod}
            </span>
          ) : (
            <span>
              {silenceTxt}
            </span>
          )}
        </MenuItem>
      )}

      {!isGuestUser && ableToKickAndBan && !isBroadcasterChat && (
        <MenuItem onClick={openKickModal}>
          <div className={styles.option}>
            <span>{translate('v2profile.kick.user')}</span>
          </div>
        </MenuItem>
      )}
      {!isGuestUser && ableToKickAndBan && !isBroadcasterChat && (
        <div>
          {isFanClubSubscribed(originalUsername) ? (
            <MenuItem onClick={openFanBanModal}>
              <div className={styles.option}>
                <span>{translate('v2profile.ban.user')}</span>
              </div>
            </MenuItem>
          ) : (
            <MenuItem onClick={openBanModal}>
              <div className={styles.option}>
                <span>{translate('v2profile.ban.user')}</span>
              </div>
            </MenuItem>
          )}
        </div>
      )}
      {showSendInboxMessage && (
        <MenuItem onClick={openInboxModal}>
          <div className={styles.option}>
            <span>{translate('v2profile.inbox.message.label')}</span>
          </div>
        </MenuItem>
      )}

      {!isGuestUser && !isFriendsTab && !isFavouritesTab && (
        <MenuItem onClick={openReportModal}>{translate('v2profile.report')}</MenuItem>
      )}

      {!isGuestUser && canDeleteConversation && (
        <MenuItem onClick={deleteConversation}>{translate('v2profile.contextual.menu.delete')}</MenuItem>
      )}

      {isBroadcasterChat &&
        !hasAccessLevel46orAbove &&
        !isFriendsTab &&
        !isFavouritesTab &&
        !isUserList &&
        !isConversation && (
        <MenuItem disabled={spamMarkActive || spamMarkDisabled} onClick={handleMarkAsSpam}>
          {spamMarkActive ? translate('v2broadcast.marked.as.spam') : translate('v2broadcast.mark.as.spam')}
          <FlagIcon className={styles.flagIcon} />
        </MenuItem>
      )}

      {isFriendsTab && !isUserInRoster(originalUsername) && (
        <MenuItem onClick={onInviteRoom}>
          {translate('v2broadcast.invite.to.room')}
        </MenuItem>
      )}

      {hasAdminPrivileges && (
        <div>
          {isFanClubSubscribed(originalUsername) ? (
            <MenuItem onClick={openFanPermaBanModal}>{translate('v2profile.chat.permanent.ban.title')}</MenuItem>
          ) : (
            <MenuItem onClick={openPermaBanModal}>{translate('v2profile.chat.permanent.ban.title')}</MenuItem>
          )}
          <MenuItem onClick={openDeleteKickModal}>{translate('v2profile.chat.delete.kick.title')}</MenuItem>
          {isPerformer && (
            <MenuItem onClick={openCloseRoomModal}>{translate('v2profile.chat.close.room.title')}</MenuItem>
          )}
          {isPerformer && (
            <MenuItem onClick={openFeatureBroadcasterModal}>
              {translate('v2profile.chat.feature.broadcaster.title')}
            </MenuItem>
          )}
        </div>
      )}
    </Menu>
  ) : null;
});

ContextualMenu.propTypes = {
  close: PropTypes.func.isRequired,
  originalUsername: PropTypes.string.isRequired,
  ableToPeek: PropTypes.bool,
  anchorEl: PropTypes.object,
  canDeleteConversation: PropTypes.bool,
  confirmConversationDelete: PropTypes.func,
  deleteConversation: PropTypes.func,
  handlePeek: PropTypes.func,
  isBroadcasterChat: PropTypes.bool,
  isFavoritesTab: PropTypes.bool,
  isUserList: PropTypes.bool,
  listedUserAccessLevel: PropTypes.string,
  message: PropTypes.object,
  openContextualMenu: PropTypes.func,
  showContextualMenu: PropTypes.bool,
};

export default ContextualMenu;
